import {
  Foreman_Read,
  Foreman_Read_Nested,
  Foreman_Read_Typeahead,
  TimeZone,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { CompanyBasic } from '~hooks/useCompany/models';
import { CompanyShareBasic } from '~hooks/useCompanyShares/models';
import { BasicLabel } from '~hooks/useLabels/models';

export class ForemanTypeahead {
  constructor(
    private _id: string,
    private _first_name: string,
    private _last_name: string,
    private _company_share: CompanyShareBasic | null,
    private _phone: string | null,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }

  public get companyShare() {
    return this._company_share;
  }

  public get phone() {
    return this._phone;
  }

  public get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  public static parse(proto: Foreman_Read_Typeahead) {
    return new ForemanTypeahead(
      proto.id,
      proto.first_name,
      proto.last_name,
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.phone,
    );
  }
}

class ForemanBasic extends ForemanTypeahead {
  constructor(
    _id: string,
    _first_name: string,
    _last_name: string,
    _company_share: CompanyShareBasic | null,
    _phone: string | null,
    private _email: string | null,
    private _company: CompanyBasic,
    private _discarded_at: Dayjs | null,
  ) {
    super(_id, _first_name, _last_name, _company_share, _phone);
  }

  public get email() {
    return this._email;
  }

  public get company() {
    return this._company;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Foreman_Read_Nested) {
    return new ForemanBasic(
      proto.id,
      proto.first_name,
      proto.last_name,
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.phone,
      proto.email,
      CompanyBasic.parse(proto.company),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class Foreman extends ForemanBasic {
  constructor(
    _id: string,
    _first_name: string,
    _last_name: string,
    _company_share: CompanyShareBasic | null,
    _phone: string | null,
    _email: string | null,
    _company: CompanyBasic,
    _discarded_at: Dayjs | null,
    private _external_id: string | null,
    private _time_zone: TimeZone,
    private _managed: boolean,
    private _labels: BasicLabel[] = [],
  ) {
    super(
      _id,
      _first_name,
      _last_name,
      _company_share,
      _phone,
      _email,
      _company,
      _discarded_at,
    );
  }

  public get externalId() {
    return this._external_id;
  }

  public get timeZone() {
    return this._time_zone;
  }

  public get managed() {
    return this._managed;
  }

  public get labels() {
    return this._labels;
  }

  public static parse(proto: Foreman_Read) {
    return new Foreman(
      proto.id,
      proto.first_name,
      proto.last_name,
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.phone,
      proto.email,
      CompanyBasic.parse(proto.company),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.external_id,
      proto.time_zone,
      proto.managed,
      proto.labels.map((label) => BasicLabel.parse(label)),
    );
  }
}
